import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../context/StoreContext';
import { useCustomerNotifications } from '../../context/CustomerNotificationsContext';
import { useSupportChat } from '../../context/SupportChatContext';
import {
  CUSTOMER_NOTIFICATIONS_PAGE_SIZE,
  getCustomerInAppNotifications,
} from '../../services/customerInAppNotificationService';
import { formatUtcDateTime } from '../../utils/dateFormatUtils';
import { buildCustomerRoute } from '../../utils/storeUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';
import PushNotificationControl from '../../components/notifications/PushNotificationControl';
import { openPushPrompt } from '../../utils/pushPrompt';

const isChatNotification = (notification) => {
  const type = String(notification?.type ?? '');
  return type === 'SupportChat' || type === '0';
};

const CustomerNotificationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { storeId } = useStore();
  const { openChat } = useSupportChat();
  const { markRead, markAllRead, unreadCount } = useCustomerNotifications();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadNotifications = useCallback(async () => {
    if (!storeId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(false);
      const result = await getCustomerInAppNotifications(storeId, {
        pageIndex: 0,
        itemsPerPage: CUSTOMER_NOTIFICATIONS_PAGE_SIZE,
      });
      setNotifications(result.data);
    } catch (loadError) {
      console.error('Failed to load customer notifications', loadError);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    let cancelled = false;
    loadNotifications().catch(() => {
      if (!cancelled) setError(true);
    });
    return () => {
      cancelled = true;
    };
  }, [loadNotifications]);

  const handleClick = async (notification) => {
    if (isChatNotification(notification)) {
      openChat();
    } else {
      const target = buildCustomerRoute(storeId, 'my-appointments');
      if (target) navigate(target);
    }
    if (!notification.isRead) {
      setNotifications((current) => current.map((item) => (
        item.id === notification.id ? { ...item, isRead: true } : item
      )));
      try {
        await markRead(notification.id);
      } catch (markError) {
        console.error('Failed to mark customer notification as read', markError);
      }
    }
  };

  const handleMarkAll = async () => {
    setNotifications((current) => current.map((item) => ({ ...item, isRead: true })));
    try {
      await markAllRead();
    } catch (markError) {
      console.error('Failed to mark customer notifications as read', markError);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: BRAND.text }}>
            {t('customerNotifications.page.title')}
          </Typography>
          <Typography variant="body2" sx={{ color: BRAND.textMuted, mt: 0.5 }}>
            {t('customerNotifications.page.subtitle')}
          </Typography>
        </Box>
        {unreadCount > 0 ? (
          <Button onClick={handleMarkAll} sx={{ color: BRAND.accentText, textTransform: 'none', flexShrink: 0 }}>
            {t('customerNotifications.markAllRead')}
          </Button>
        ) : null}
      </Box>
      <Box sx={{ mb: 2 }}>
        <PushNotificationControl
          audience="customer"
          onEnableRequest={() => openPushPrompt('customer')}
        />
      </Box>
      {error ? <Alert severity="error" sx={{ mb: 2 }}>{t('customerNotifications.page.error.loading')}</Alert> : null}
      {!loading && !error && notifications.length === 0 ? (
        <Typography variant="body2" sx={{ color: BRAND.textMuted }}>
          {t('customerNotifications.empty')}
        </Typography>
      ) : null}
      <List disablePadding>
        {notifications.map((notification) => (
          <ListItemButton
            key={notification.id}
            onClick={() => handleClick(notification)}
            sx={{
              alignItems: 'flex-start',
              borderRadius: 1,
              mb: 1,
              bgcolor: notification.isRead ? 'transparent' : brandAlpha('accent', 0.08),
            }}
          >
            <ListItemText
              primary={notification.title}
              secondary={(
                <>
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                    {notification.body}
                  </Typography>
                  <Typography component="span" variant="caption" color="text.disabled">
                    {formatUtcDateTime(notification.createdAtUtc)}
                  </Typography>
                </>
              )}
              primaryTypographyProps={{ fontWeight: notification.isRead ? 500 : 700 }}
            />
          </ListItemButton>
        ))}
      </List>
    </Box>
  );
};

export default CustomerNotificationsPage;
